import { type ReactElement, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { useSignOut } from '@/features/sign-out';
import { MemberAvatar } from '@/entities/session';

const ITEMS = [
  { to: '/my', label: '마이페이지' },
  { to: '/my/downloads', label: '받은 자료' },
  { to: '/my/account', label: '계정' },
] as const;

/**
 * 아바타 메뉴.
 *
 * 호버로 열되 **클릭·키보드로도 열린다.** 호버만으로 여는 메뉴는 터치와 키보드에서 통째로 막힌다.
 * 아바타 자체는 여전히 마이페이지로 가는 버튼이라, 메뉴가 안 열려도 갈 곳은 잃지 않는다.
 */
export function MemberMenu(): ReactElement {
  const menuId = useId();
  const signOut = useSignOut();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 아바타에서 메뉴로 커서를 옮기는 사이에 닫히면 누를 수가 없다
  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 호버는 보조 수단 — 열고 닫는 정본은 버튼과 Esc다
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={closeSoon}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        aria-label="내 계정"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        // 토글하지 않는다 — 포커스가 먼저 열어 둔 것을 클릭이 도로 닫는다. 닫기는 Esc·바깥으로 맡긴다
        onClick={openNow}
        className="block rounded-full ring-offset-2 ring-offset-background hover:ring-2 hover:ring-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
      >
        <MemberAvatar />
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 top-full z-30 mt-2 w-40 overflow-hidden rounded-md border border-border bg-card py-1 shadow-lg"
        >
          {ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-[13.5px] hover:bg-secondary"
            >
              {item.label}
            </Link>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
            className="block w-full border-border border-t px-4 py-2 text-left text-[13.5px] text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            로그아웃
          </button>
        </div>
      ) : null}
    </div>
  );
}
